import React, { useRef, useState } from "react";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { Toast } from "primereact/toast";
import Editor from "@monaco-editor/react";
import { Button } from "primereact/button";
import * as monaco from "monaco-editor";
import {
  MetaOptions,
  PageLayout,
  TimingOptions,
} from "api/model/dashboard/DashboardReportOptions";
import { TabPanel, TabView } from "primereact/tabview";
import { DashboardTab } from "api/model/dashboard/DashboardTab";
import DashboardTabsSubForm from "./DashboardReportTabEditor/DashboardReportTabEditor";
import { MetaOptionsSubForm } from "./DashboardReportEditorComponents/DashboardReportEditorForms/MetaOptionsSubForm";
import { PageLayoutSubForm } from "./DashboardReportEditorComponents/DashboardReportEditorForms/PageLayoutSubForm";
import { TimingOptionsSubForm } from "./DashboardReportEditorComponents/DashboardReportEditorForms/TimingOptionsSubForm";
import { GeneralSubForm } from "./DashboardReportEditorComponents/DashboardReportEditorForms/GeneralSubFrom";

export const DashboardReportEditor = (props: {
  dashboard: Dashboard;
  cancel: () => void;
  onChange: (updatedConfig: Dashboard) => void;
}) => {
  const [def, setDef] = useState<string | undefined>(
    JSON.stringify(props.dashboard, null, 2)
  );
  const [config, setConfig] = useState<Dashboard>(props.dashboard);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const toast = useRef<Toast>(null);
  const [newGeneralSub, setNewGeneralSub] = useState<object>({});

  const handleSave = () => {
    const updatedConfig = {
      ...config,
      ...newGeneralSub,
    };
    setConfig(updatedConfig);
    props.onChange(updatedConfig);
  };

  const updateConfig = (dashboard: Dashboard) => {
    setConfig(dashboard);
    setDef(JSON.stringify(dashboard, null, 2));
  };

  const onEditorChange = (
    newValue: string | undefined,
    e: monaco.editor.IModelContentChangedEvent
  ) => {
    setDef(newValue);
    try {
      const val = JSON.parse(newValue!!);
      setConfig(val);
      setErrorMessage(undefined);
    } catch (e: any) {
      setErrorMessage("Failed to parse JSON: " + e.message);
    }
  };

  const onChangePageLayout = (pageLayout: PageLayout) => {
    const cfgNew = {
      ...config,
      reportOptions: { ...config.reportOptions, pageLayout: pageLayout },
    };
    updateConfig(cfgNew);
  };

  const onChangeTimingOptions = (timingOptions: TimingOptions) => {
    const cfgNew = {
      ...config,
      reportOptions: { ...config.reportOptions, timingOptions: timingOptions },
    };
    updateConfig(cfgNew);
  };

  const onChangeMetaOptions = (metaOptions?: MetaOptions) => {
    const cfgNew = {
      ...config,
      reportOptions: { ...config.reportOptions, meta: metaOptions },
    } as Dashboard;
    updateConfig(cfgNew);
  };

  const onChangeDashboardTabs = (tabs: Array<DashboardTab>) => {
    const cfgNew: Dashboard = {
      ...config,
      dashboardTabsJs: tabs,
    };
    updateConfig(cfgNew);
  };

  return (
    <div className="flex flex-column">
      <Toast ref={toast} />
      <TabView
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
      >
        <TabPanel header="General">
          <div>
            <GeneralSubForm
              dashboard={config}
              setNewGeneralSub={setNewGeneralSub}
            />
          </div>
          <div>
            <TimingOptionsSubForm
              timingOptions={config?.reportOptions.timingOptions}
              onChange={onChangeTimingOptions}
            />
          </div>
          <div>
            <PageLayoutSubForm
              pageLayout={config?.reportOptions.pageLayout!!}
              onChange={onChangePageLayout}
            />
          </div>
          <div>
            <MetaOptionsSubForm
              metaOptions={config?.reportOptions.meta}
              onChange={onChangeMetaOptions}
            />
          </div>
        </TabPanel>
        <TabPanel header="Tabs">
          <DashboardTabsSubForm
            dashboardTabs={config?.dashboardTabsJs}
            onChange={onChangeDashboardTabs}
          />
        </TabPanel>
        <TabPanel header="JSON">
          <Editor
            height="500px"
            width="100%"
            defaultLanguage="json"
            value={def}
            onChange={onEditorChange}
            theme="vs-dark"
          />
        </TabPanel>
      </TabView>

      {errorMessage && <div className="p-error">{errorMessage}</div>}

      <div
        className="flex-none flex justify-content-center gap-2 mt-2"
        style={{ width: "100%" }}
      >
        <Button
          label="Save"
          className="p-button-success w-full"
          icon="pi pi-check"
          onClick={handleSave}
          disabled={errorMessage != undefined}
        />
        <Button
          label="Cancel"
          className="p-button-danger w-full"
          icon="pi pi-times"
          onClick={props.cancel}
        />
      </div>
    </div>
  );
};
